<div class="drf">

  <button type="button" class="drf-btn" [class.unset]="optional && !active" (click)="toggle($event)">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
         stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
    @if (optional && !active) {
      <span class="k">Any time</span>
    } @else if (key === 'Any Time') {
      <span class="k">Any Time</span>
    } @else {
      <span class="k">{{ key }}</span>
      <span class="sep">·</span>
      <span class="dates">{{ buttonLabel }}</span>
    }
    <svg class="caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M6 9l6 6 6-6"/>
    </svg>
  </button>

  @if (open) {
    <div class="pop" [class.pop-left]="effectiveAlign === 'left'" [class.placed]="popPlaced"
         [style.transform]="popShift ? 'translateX(' + popShift + 'px)' : null" (click)="stop($event)">

      <!-- Phones only (hidden by CSS elsewhere): the popover becomes a full-screen
           sheet there, so there is no "outside" left to tap to dismiss it. -->
      <button type="button" class="pop-close" aria-label="Close" (click)="open = false">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
             stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>

      <!-- Preset rail -->
      <div class="rail">
        @if (optional) {
          <!-- "Any time" = no bound at all, and the selected state while the
               filter is unset (console-v2's own rail head). It clears rather than
               resolving to a span: a filter that still sends 2022-01-01 → today
               would hide older rows and hold the filter count at 1 while the
               trigger read "Any time". -->
          <div class="group any-group">
            <button type="button" class="preset" [class.on]="!active" (click)="clearToAny()">Any time</button>
          </div>
        } @else {
          <!-- Non-optional (the dashboard): every request must carry a concrete
               span, so "all data" is a preset, not an absence. -->
          <div class="group">
            <div class="g-label">All</div>
            <button type="button" class="preset" [class.on]="active && key === 'Any Time'"
                    (click)="selectPreset('Any Time')">Any Time</button>
          </div>
        }
        @for (group of presetGroups; track group.label) {
          <div class="group">
            <div class="g-label">{{ group.label }}</div>
            @for (item of group.items; track item) {
              <button type="button" class="preset" [class.on]="active && key === item" (click)="selectPreset(item)">
                {{ item }}
              </button>
            }
          </div>
        }
        <div class="group custom-group">
          <button type="button" class="preset" [class.on]="active && key === 'Custom'" (click)="selectPreset('Custom')">
            Custom range…
          </button>
        </div>
      </div>

      @if (key === 'Custom') {
        <!-- Two-month range calendar -->
        <div class="cal-wrap">
          <div class="cal-head">
            <button type="button" class="nav" (click)="shiftMonths(-1)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <span class="grow"></span>
            <button type="button" class="nav" (click)="shiftMonths(1)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 6l6 6-6 6"/>
              </svg>
            </button>
          </div>

          <div class="cals">
            @for (month of months; track month.getTime()) {
              <div class="cal">
                <div class="cal-month">{{ fmtMonth(month) }}</div>
                <div class="cal-grid">
                  @for (d of dow; track d) {
                    <span class="dowc">{{ d }}</span>
                  }
                  @for (week of weeks(month); track $index) {
                    @for (cell of week; track $index) {
                      @if (cell.date; as date) {
                        <button type="button" class="day"
                                [class.end]="isEndpoint(date)"
                                [class.mid]="inRange(date)"
                                (click)="pick(date)">{{ date.getDate() }}</button>
                      } @else {
                        <span class="day blank"></span>
                      }
                    }
                  }
                </div>
              </div>
            }
          </div>

          <div class="cal-foot">
            <span class="cal-sum">{{ customSummary }}</span>
            <span class="grow"></span>
            <button type="button" class="btn-primary btn-xs" [disabled]="!customFrom || !customTo" (click)="apply()">
              Apply
            </button>
          </div>
        </div>
      } @else {
        <!-- Preset summary -->
        <div class="summary">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
          </svg>
          <!-- Reads `active`, NOT `key`. `key` holds the last preset that was picked and stays
               put when the filter is cleared, so a summary keyed off it went on announcing
               "Showing Yesterday" over yesterday's dates while the trigger, the rail highlight
               and the emitted range had all moved to "Any time". `active` is what those three
               already agree on, so the summary follows it too. Same copy the non-optional
               'Any Time' preset uses — both mean "no date bound". -->
          <div class="sum-title">Showing {{ optional && !active ? 'Any time' : key }}</div>
          @if (key === 'Any Time' || (optional && !active)) {
            <div class="sum-dates">All available data</div>
          } @else {
            <div class="sum-dates">{{ summaryLabel }}</div>
          }
          <div class="sum-hint">Pick “Custom range” for a specific date span</div>
        </div>
      }

    </div>
  }

</div>
